import {DescriptionWithImages, HandoverModel} from "@handover/view-models/handover.model";
import {GetHandoverResponse, RawHandoverPart} from "@handover/http-services/responses/get-handover.response";

import {environment} from "@src/environments/environment";

export class GetHandoverResponseMapper {
  mapToHandoverModel(response: GetHandoverResponse, isGiven: boolean): HandoverModel {
    const model = {} as HandoverModel;
    model.isGiven = isGiven;
    model.driverFullName = `${response?.WN} ${response.WS}`;
    model.phone = response.WP;
    model.statement = response.WST;
    model.isCreatedOnDesktop = response.BG.QR === "";

    if (response.HE)
      this.setClientData(response, model);

    // Zwracane przez api będą zawsze 2 obiekty o ile został już wykonany zwrot.
    // Wyświetlane jest tylko przekazanie lub tylko zwrot i potrzebna jest informacja z zewnątrz co ma zostać zmapowane do modelu protokołu dla UI.
    const givenRaw = response?.BG;
    model.givenId = givenRaw?.HID;
    const returnRaw = response?.ED;
    model.returnId = returnRaw?.HID;
    if (isGiven) {
      this.mapRawHandoverToHandoverModel(givenRaw, model);
    } else {
      this.mapRawHandoverToHandoverModel(returnRaw, model);
    }

    this.setAllImagesCount(model);

    return model;
  }

  private setClientData(response: GetHandoverResponse, model: HandoverModel) {
    model.clientData = [];
    model.clientData.push(response?.HE.line1);
    model.clientData.push(response?.HE.line2);
    model.clientData.push(response?.HE.line3);
  }

  private mapRawHandoverToHandoverModel(rawHandover: RawHandoverPart, model: HandoverModel) {
    model.uniqueNumber = rawHandover.NR;
    model.place = rawHandover?.PL;
    model.date = rawHandover?.DA;
    model.createDate = rawHandover?.DAC;
    model.workerIdNumber = rawHandover?.WC;
    model.vehicleName = rawHandover?.NM;
    model.fuel = rawHandover?.FL;
    model.mileage = rawHandover?.OV;
    model.equipmentItems = rawHandover?.EQ.map(x => x?.NA);
    model.transferringPerson = `${rawHandover.SN} ${rawHandover.SS}`;
    model.token = rawHandover.QR;

    this.setGeneralSection(rawHandover, model);
    this.setDamagesSection(rawHandover, model);
    this.setCommentsSection(rawHandover, model);
    this.setSignatureSection(rawHandover, model);
  }

  private setCommentsSection(rawHandover: RawHandoverPart, model: HandoverModel) {
    const section = rawHandover?.PG.find(x => x.TP == HandoverGalleryType.comment);
    if (section) {
      model.comments = [];
      for (let img of section?.IMG) {
        const comment = {} as DescriptionWithImages;
        comment.desc = img?.DS;
        comment.imageUrls = img.PIC.map(x => this.getImageSrc(model.givenId, x?.ESI));
        model.comments.push(comment);
      }
    }
  }

  private setDamagesSection(rawHandover: RawHandoverPart, model: HandoverModel) {
    const section = rawHandover?.PG.find(x => x.TP == HandoverGalleryType.damage);
    if (section) {
      model.damages = [];
      for (let img of section?.IMG) {
        const damage = {} as DescriptionWithImages;
        damage.desc = img?.DS;
        damage.imageUrls = img.PIC.map(x => this.getImageSrc(model.givenId, x?.ESI));
        model.damages.push(damage);
      }
    }
  }

  private setGeneralSection(rawHandover: RawHandoverPart, model: HandoverModel) {
    const section = rawHandover?.PG.find(x => x.TP == HandoverGalleryType.general);
    if (section) {
      model.general = {} as DescriptionWithImages;
      model.general.desc = section?.IMG[0].DS;
      model.general.imageUrls = section?.IMG[0].PIC.map(x => this.getImageSrc(model.givenId, x?.ESI));
    }
  }

  private setSignatureSection(rawHandover: RawHandoverPart, model: HandoverModel) {
    const section = rawHandover?.PG.find(x => x.TP == HandoverGalleryType.signature);
    if (section) {
      model.driverSignatureUrl = section?.IMG[0].PIC.map(x => this.getImageSrc(model.givenId, x?.ESI))[0];
    }
  }

  private getImageSrc(handoverId: string, imageId: string): string {
    return `${environment.logosApiUrl}/handovers/${handoverId}/photos/${imageId.replace('"', '').replace('"', '')}`;
  }

  private setAllImagesCount(model: HandoverModel) {
    let count = model.general?.imageUrls.length ?? 0;

    if (model.damages) {
      for (let damage of model.damages) {
        count += damage?.imageUrls?.length ?? 0;
      }
    }

    if (model.comments) {
      for (let comment of model.comments) {
        count += comment?.imageUrls?.length ?? 0;
      }
    }

    model.allImagesCount = count;
  }

}

enum HandoverGalleryType {
  signature = 1,
  general = 2,
  damage = 3,
  comment = 4
}
